import { z } from 'zod';

import { employeeStatusValues, employmentTypeValues } from '../employees/employees.dto.js';
import {
  GlobalMaterialDtoSchema,
  GlobalMaterialPriceVersionRowSchema,
  MaterialOverrideDtoSchema,
  MaterialPriceOverrideVersionRowSchema,
} from '../materials-catalog/materials-catalog.schema.js';
import {
  MaterialCategoryConfigDocumentSchema,
  MaterialOrderingConfigDocumentSchema,
} from '../materials-presentation/materials-presentation.schema.js';
import {
  type ApplyImportPackRequest,
  type ApplyImportPackResponse,
  type IssueImportPackRequest,
  type IssueImportPackResponse,
  type IssueImportPackSummary,
  type ImportPackCatalogPayload,
  type ImportPackEmployeePayload,
  type ImportPackEmployeesPayload,
  type ImportPackEnvelopeV1,
  importPackApplyStatusValues,
  issueImportPackTypeValues,
  importPackTypeValues,
} from './import-packs.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();
const employeeNumberSchema = z.string().trim().min(1).max(50);
const timeZoneSchema = z.string().trim().min(1).refine(isValidIanaTimeZone, { message: 'Invalid time zone' });

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export const ImportPackEmployeePayloadSchema: z.ZodType<ImportPackEmployeePayload> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid().nullable(),
    userId: z.uuid().nullable(),
    employeeNumber: employeeNumberSchema.nullable(),
    timezone: timeZoneSchema.nullable(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    displayName: z.string().min(1),
    status: z.enum(employeeStatusValues),
    hireDate: dateSchema.nullable(),
    terminationDate: dateSchema.nullable(),
    employmentType: z.enum(employmentTypeValues).nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strict();

export const ImportPackEmployeesPayloadSchema: z.ZodType<ImportPackEmployeesPayload> = z
  .object({
    employees: z.array(ImportPackEmployeePayloadSchema).nonempty(),
  })
  .strict();

export const ImportPackCatalogPayloadSchema: z.ZodType<ImportPackCatalogPayload> = z
  .object({
    materials: z.array(GlobalMaterialDtoSchema).nonempty(),
    overrides: z.array(MaterialOverrideDtoSchema),
    globalPriceVersions: z.array(GlobalMaterialPriceVersionRowSchema).nonempty(),
    overridePriceVersions: z.array(MaterialPriceOverrideVersionRowSchema),
    companyOrdering: MaterialOrderingConfigDocumentSchema.nullable(),
    companyCategories: MaterialCategoryConfigDocumentSchema.nullable(),
  })
  .strict();

export const ImportPackEnvelopeV1Schema: z.ZodType<ImportPackEnvelopeV1> = z
  .object({
    version: z.literal(1),
    id: z.uuid(),
    type: z.enum(importPackTypeValues),
    companyId: z.uuid(),
    locationId: z.uuid().nullable(),
    issuedAt: timestampSchema,
    payload: z.unknown(),
    publicKeyId: z.string().trim().min(1).nullable(),
    signature: z.string().trim().min(1).nullable(),
  })
  .strict();

export const IssueImportPackRequestSchema: z.ZodType<IssueImportPackRequest> = z.discriminatedUnion(
  'type',
  [
    z.object({
      companyId: z.uuid(),
      type: z.literal('employees'),
      storeId: z.uuid(),
      locationId: z.uuid().nullable(),
    }).strict(),
    z.object({
      companyId: z.uuid(),
      type: z.literal('catalog'),
      storeId: z.uuid(),
      locationId: z.null(),
    }).strict(),
  ],
);

export const IssueImportPackSummarySchema: z.ZodType<IssueImportPackSummary> = z.object({
  type: z.enum(issueImportPackTypeValues),
  employeeCount: z.number().int().min(0),
  materialCount: z.number().int().min(0),
  overrideCount: z.number().int().min(0),
  globalPriceVersionCount: z.number().int().min(0),
  overridePriceVersionCount: z.number().int().min(0),
  hasCompanyOrdering: z.boolean(),
  hasCompanyCategories: z.boolean(),
});

export const IssueImportPackResponseSchema: z.ZodType<IssueImportPackResponse> = z.object({
  envelope: ImportPackEnvelopeV1Schema,
  summary: IssueImportPackSummarySchema,
});

export const ApplyImportPackRequestSchema: z.ZodType<ApplyImportPackRequest> = ImportPackEnvelopeV1Schema;

export const ApplyImportPackResponseSchema: z.ZodType<ApplyImportPackResponse> = z.object({
  packId: z.uuid(),
  status: z.enum(importPackApplyStatusValues),
  appliedAt: timestampSchema.nullable(),
  appliedCount: z.number().int().min(0),
  rejectedCount: z.number().int().min(0),
  warnings: z.array(z.string()),
});
